import React, { useRef } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from "chart.js";
import { Bar, Pie } from "react-chartjs-2";
import html2canvas from "html2canvas";
import MapillaryImage from "./MapillaryImage";
import "../styles/FacilityDetailModal.css";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const FacilityDetailModal = ({ isOpen, onClose, facility }) => {
  const chartsRef = useRef(null);

  if (!isOpen || !facility) return null;

  const reports = facility.reports || [facility];
  const airReleases = parseFloat(facility.airReleases || 0);
  const waterReleases = parseFloat(facility.waterReleases || 0);
  const landReleases = parseFloat(facility.landReleases || 0);
  const totalReleases = airReleases + waterReleases + landReleases;

  // CSV download for this facility
  const handleDownloadCSV = () => {
    const headers = [
      "Facility Name",
      "Parent Company",
      "Address",
      "City",
      "County",
      "State",
      "ZIP",
      "Industry",
      "Chemical",
      "Carcinogen",
      "Air Releases (lbs)",
      "Water Releases (lbs)",
      "Land Releases (lbs)",
      "Total Releases (lbs)",
    ];

    const rows = reports.map((r) => [
      facility.facilityName,
      facility.parentCompany || "",
      facility.streetAddress,
      facility.city,
      facility.county,
      facility.state,
      facility.zip,
      facility.industrySector,
      r.chemical,
      r.carcinogen,
      r.airReleases,
      r.waterReleases,
      r.landReleases,
      r.totalReleases,
    ]);

    const csvContent = [headers, ...rows]
      .map((row) => row.map((val) => `"${val}"`).join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${facility.facilityName.replace(/\s+/g, "_")}_TRI_Report.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Image download of charts section
  const handleDownloadImage = async () => {
    if (!chartsRef.current) return;
    const canvas = await html2canvas(chartsRef.current, {
      backgroundColor: "#ffffff",
      scale: 2, // higher resolution
    });
    const url = canvas.toDataURL("image/png");
    const a = document.createElement("a");
    a.href = url;
    a.download = `${facility.facilityName.replace(/\s+/g, "_")}_Charts.png`;
    a.click();
  };

  const barData = {
    labels: ["Air", "Water", "Land"],
    datasets: [
      {
        label: "Releases (lbs)",
        data: [airReleases, waterReleases, landReleases],
        backgroundColor: [
          "rgba(52, 152, 219, 0.7)",
          "rgba(46, 204, 113, 0.7)",
          "rgba(155, 89, 182, 0.7)",
        ],
        borderColor: [
          "rgba(52, 152, 219, 1)",
          "rgba(46, 204, 113, 1)",
          "rgba(155, 89, 182, 1)",
        ],
        borderWidth: 2,
      },
    ],
  };

  const pieData = {
    labels: ["Air", "Water", "Land"],
    datasets: [
      {
        data: [airReleases, waterReleases, landReleases],
        backgroundColor: [
          "rgba(52, 152, 219, 0.8)",
          "rgba(46, 204, 113, 0.8)",
          "rgba(155, 89, 182, 0.8)",
        ],
        borderColor: [
          "rgba(52, 152, 219, 1)",
          "rgba(46, 204, 113, 1)",
          "rgba(155, 89, 182, 1)",
        ],
        borderWidth: 2,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (context) => `${context.parsed.y.toLocaleString()} lbs`,
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: (value) => value.toLocaleString() + " lbs",
        },
      },
    },
  };

  const pieOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "bottom" },
      tooltip: {
        callbacks: {
          label: (context) => {
            const percentage =
              totalReleases > 0
                ? ((context.parsed / totalReleases) * 100).toFixed(1)
                : "0.0";
            return `${
              context.label
            }: ${context.parsed.toLocaleString()} lbs (${percentage}%)`;
          },
        },
      },
    },
  };

  return (
    <div className="facility-modal-overlay" onClick={onClose}>
      <div
        className="facility-modal-content"
        onClick={(e) => e.stopPropagation()}
      >
        <button className="facility-modal-close" onClick={onClose}>
          ✕
        </button>

        <div className="facility-modal-header">
          <h2>{facility.facilityName}</h2>
          <p className="company-name">{facility.parentCompany}</p>
          <p className="facility-address">
            {facility.streetAddress}, {facility.city}, {facility.county} County
          </p>
        </div>

        <div className="facility-info-grid">
          <div className="info-card">
            <span className="info-label">Industry</span>
            <span className="info-value">{facility.industrySector}</span>
          </div>
          <div className="info-card">
            <span className="info-label">Chemicals Reported</span>
            <span className="info-value">{reports.length}</span>
          </div>
          <div className="info-card">
            <span className="info-label">Total Releases</span>
            <span className="info-value">
              {totalReleases.toLocaleString()} lbs
            </span>
          </div>
          <div
            className={`info-card ${
              reports.some((r) => r.carcinogen === "YES")
                ? "carcinogen"
                : "non-carcinogen"
            }`}
          >
            <span className="info-label">Carcinogen Status</span>
            <span className="info-value">
              {reports.some((r) => r.carcinogen === "YES")
                ? "⚠️ Contains Carcinogens"
                : "✓ No Carcinogens"}
            </span>
          </div>
        </div>

        {/* Charts section — wrapped in ref for image capture.
            Hidden entirely when the facility reported no quantifiable
            releases (all air/water/land values are 0 or blank in EPA data),
            since the charts would otherwise render blank. */}
        {totalReleases > 0 && (
          <div ref={chartsRef}>
            <div className="charts-capture-header">
              <p className="capture-facility-name">{facility.facilityName}</p>
              <p className="capture-subtitle">EPA TRI 2024 — Release Breakdown</p>
            </div>
            <div className="charts-container">
              <div className="chart-section">
                <h3>Total Release Breakdown by Type</h3>
                <div className="chart-wrapper">
                  <Bar data={barData} options={chartOptions} />
                </div>
              </div>
              <div className="chart-section">
                <h3>Release Distribution</h3>
                <div className="chart-wrapper pie-chart">
                  <Pie data={pieData} options={pieOptions} />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Street-level imagery near the facility (Mapillary) */}
        <MapillaryImage
          latitude={facility.latitude}
          longitude={facility.longitude}
          variant="full"
        />

        <div className="release-details">
          <h3>Chemical-by-Chemical Breakdown</h3>
          <table className="release-table">
            <thead>
              <tr>
                <th>Chemical</th>
                <th>Carcinogen</th>
                <th>Air (lbs)</th>
                <th>Water (lbs)</th>
                <th>Land (lbs)</th>
                <th>Total (lbs)</th>
              </tr>
            </thead>
            <tbody>
              {reports.map((r, i) => (
                <tr key={i}>
                  <td>{r.chemical}</td>
                  <td>
                    {r.carcinogen === "YES" ? (
                      <span className="carcinogen-badge">⚠️ Yes</span>
                    ) : (
                      <span className="non-carcinogen-badge">✓ No</span>
                    )}
                  </td>
                  <td>{r.airReleases.toLocaleString()}</td>
                  <td>{r.waterReleases.toLocaleString()}</td>
                  <td>{r.landReleases.toLocaleString()}</td>
                  <td>
                    <strong>{r.totalReleases.toLocaleString()}</strong>
                  </td>
                </tr>
              ))}
              <tr className="totals-row">
                <td>
                  <strong>TOTAL</strong>
                </td>
                <td></td>
                <td>
                  <strong>{airReleases.toLocaleString()}</strong>
                </td>
                <td>
                  <strong>{waterReleases.toLocaleString()}</strong>
                </td>
                <td>
                  <strong>{landReleases.toLocaleString()}</strong>
                </td>
                <td>
                  <strong>{totalReleases.toLocaleString()}</strong>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Download buttons */}
        <div className="modal-download-section">
          <button className="download-csv-button" onClick={handleDownloadCSV}>
            📄 Download CSV Report
          </button>
          {totalReleases > 0 && (
            <button
              className="download-image-button"
              onClick={handleDownloadImage}
            >
              🖼️ Download Chart Image
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default FacilityDetailModal;
